import Modal from '@components/Modal';
import DefaultButton from '@components/Atoms/DefaultButton';
import defaultButtonStyles from '@components/Atoms/DefaultButton/index.module.scss';
import SelectComponent from '@components/Atoms/SelectComponent';
import { SelectableOptionType } from 'custom';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { MultiValue, SingleValue } from 'react-select';
import styles from './index.module.scss';

const TITLE_ID = 'request-expert-credits-modal-title';
const DESCRIPTION_ID = 'request-expert-credits-modal-description';

const CREDIT_OPTIONS: SelectableOptionType[] = Array.from({ length: 10 }, (_, i) => {
  const n = i + 1;

  return {
    value: n,
    label: n === 1 ? '1 credit' : `${n} credits`,
  };
});

type SelectOnChangeValue = SingleValue<SelectableOptionType> | MultiValue<SelectableOptionType>;

function selectValueToSingleOption(value: SelectOnChangeValue): SelectableOptionType | null {
  if (value == null || Array.isArray(value)) return null;

  return value as SelectableOptionType;
}

export interface RequestExpertCreditsModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Submit handler; modal closes after this resolves unless it throws.
   * Wire your POST anonymous credits request here (and refetch balances as needed).
   */
  onSend: (requestedCredits: number) => void | Promise<void>;
}

export const RequestExpertCreditsModal: React.FC<RequestExpertCreditsModalProps> = ({
  open,
  onClose,
  onSend,
}) => {
  const [selectedCredits, setSelectedCredits] = useState<SelectableOptionType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedCredits(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSetOpen = useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  const handleSend = useCallback(async () => {
    const raw = selectedCredits?.value;
    const requested = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;

    if (!Number.isFinite(requested) || requested < 1) return;

    setIsSubmitting(true);
    try {
      await Promise.resolve(onSend(requested));
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [onClose, onSend, selectedCredits]);

  return (
    <div className={styles.root}>
      <Modal
        open={open}
        setOpen={handleSetOpen}
        overlay
        onTapBackgroundClose
        className={styles.modalShell}
        aria-labelledby={TITLE_ID}
        aria-describedby={DESCRIPTION_ID}
        role="dialog"
      >
        <div className={styles.header}>
          <h2 id={TITLE_ID} className={styles.title}>
            Request additional credits
          </h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <i className={classNames('icon', 'icon-x', styles.closeIcon)} aria-hidden="true" />
          </button>
        </div>

        <div id={DESCRIPTION_ID} className={styles.body}>
          <p className={styles.lead}>
            Your request is anonymous, your name is never shared. Your wellbeing manager will only
            see the number of credits you are requesting, how many you have used, and how many you
            have remaining.
          </p>
          <p className={styles.note}>* 1 credit = 25 minutes session time</p>

          <label htmlFor="request-expert-credits-select" className={styles.fieldLabel}>
            Number of credits requested
          </label>
          <SelectComponent
            id="request-expert-credits-select"
            name="requestExpertCreditsAmount"
            placeholder="Select amount"
            options={CREDIT_OPTIONS}
            value={selectedCredits}
            onChange={(value: SelectOnChangeValue) => {
              setSelectedCredits(selectValueToSingleOption(value));
            }}
            isSearchable={false}
            isDisabled={isSubmitting}
          />
        </div>

        <div className={styles.footer}>
          <DefaultButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            type="button"
            variant="primary"
            className={classNames(defaultButtonStyles.button, styles.sendBtn)}
            onClick={handleSend}
            disabled={selectedCredits == null || isSubmitting}
          >
            Send anonymously
          </DefaultButton>
        </div>
      </Modal>
    </div>
  );
};
